import { useEffect, useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { deskSearch, type SearchHit, type TitleStatus } from './api'
import { batchHref, memberHref, paymentHref } from './links'
import { highlight, moveSelection, resultsAreCurrent, searchReady } from './search'
import { HelpLink } from './HelpLink'

const focus = 'focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-glow'

const TITLES: { label: string; slug: string }[] = [
  { label: 'All titles', slug: '' },
  { label: 'FJ', slug: 'flight_journal' },
  { label: 'MAN', slug: 'model_airplane_news' },
  { label: 'RCCA', slug: 'rc_car_action' },
  { label: 'DCX', slug: 'diecast' },
]
const STATUSES: { label: string; value: string }[] = [
  { label: 'Any status', value: '' },
  { label: 'Active', value: 'active' },
  { label: 'Lapsed', value: 'lapsed' },
  { label: 'Cancelled', value: 'cancelled' },
  { label: 'Gift', value: 'gift' },
]
const DOT: Record<TitleStatus, string> = {
  active: 'bg-green-500',
  lapsed: 'bg-amber-glow',
  cancelled: 'bg-red-500',
  gift: 'bg-violet-500',
  other: 'bg-ink-300',
}
const STATUS_WORD: Record<TitleStatus, string> = { active: 'Active', lapsed: 'Lapsed', cancelled: 'Cancelled', gift: 'Gift', other: 'Other' }

function Chip({ on, onClick, children }: { on: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      type="button"
      aria-pressed={on}
      onClick={onClick}
      className={`rounded-full border px-3 py-1 text-[13px] font-semibold ${focus} ${on ? 'border-amber-glow bg-ink-600 text-paper' : 'border-ink-500 text-paper/70 hover:text-paper'}`}
    >
      {children}
    </button>
  )
}

function fmtUntil(d: string | null): string {
  if (!d) return ''
  const m = /^(\d{4})-(\d{2})/.exec(d)
  if (!m) return d
  const mon = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][Number(m[2]) - 1]
  return `${mon} ${m[1]}`
}

export function SearchPage() {
  const navigate = useNavigate()
  const [raw, setRaw] = useState('')
  const [q, setQ] = useState('')
  const [title, setTitle] = useState('')
  const [status, setStatus] = useState('')
  const [sel, setSel] = useState(0)

  // 200 ms debounce on the query sent to the server.
  useEffect(() => {
    const t = setTimeout(() => { setQ(raw.trim()); setSel(0) }, 200)
    return () => clearTimeout(t)
  }, [raw])

  const ready = searchReady(q)
  const { data } = useQuery({
    queryKey: ['desk-search', q, title, status],
    queryFn: async () => ({ ...(await deskSearch({ q, publication: title || undefined, status: status || undefined })), publication: title, status }),
    enabled: ready,
    placeholderData: keepPreviousData,
  })
  const rows: SearchHit[] = ready ? (data?.items ?? []) : []
  const current = ready && resultsAreCurrent({ raw, q, title, status, data })

  const showEmpty = current && rows.length === 0
  const live = !ready
    ? 'Results appear as you type'
    : !current
      ? 'Searching…'
      : `${rows.length} ${rows.length === 1 ? 'member' : 'members'} so far · updating as you type`

  function onKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (e.key === 'ArrowDown') { e.preventDefault(); setSel((i) => moveSelection(i, 1, rows.length)) }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSel((i) => moveSelection(i, -1, rows.length)) }
    else if (e.key === 'Enter' && current && rows[sel]) { e.preventDefault(); navigate(memberHref(rows[sel].subscriber_id)) }
  }

  return (
    <main className="flex min-w-0 flex-1 flex-col gap-5 px-4 pb-16 pt-10 md:px-12">
      <header className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1.5">
          <h1 className="font-serif text-4xl font-medium tracking-tight">Find a member</h1>
          <p className="max-w-[680px] leading-relaxed text-paper/70">
            Searches everyone we have ever had on any title: active, lapsed, cancelled, gift recipients, payers, and people with no email address.
          </p>
          <HelpLink page="find" />
        </div>
        <Link to={batchHref()} className={`inline-flex min-h-[44px] items-center gap-2 rounded-lg bg-paper px-4 font-bold text-ink-900 ${focus}`}>
          Mail Order Batch
        </Link>
      </header>

      <div role="search" className="flex flex-col gap-2">
        <label htmlFor="desk-q" className="sr-only">Search members</label>
        <div className="flex min-h-[56px] items-center gap-2.5 rounded-[10px] border border-ink-400 bg-ink-700 px-4">
          <input
            id="desk-q"
            type="search"
            autoFocus
            autoComplete="off"
            aria-controls="desk-results"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Start typing a name, email, account no., ZIP, phone, street, or check, money order or receipt no."
            className="min-w-0 flex-1 border-0 bg-transparent py-4 text-[17px] outline-none"
          />
          {raw && (
            <button type="button" aria-label="Clear search" onClick={() => { setRaw(''); setQ(''); setSel(0) }} className={`h-9 w-9 text-paper/60 ${focus}`}>×</button>
          )}
        </div>
        <div className="flex flex-wrap justify-between gap-2 text-[12px] text-paper/60">
          <span aria-live="polite">{live}</span>
          <span><kbd>↑</kbd> <kbd>↓</kbd> to move · <kbd>Enter</kbd> opens the highlighted member</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
        <div role="group" aria-label="Title" className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[12px] font-bold uppercase tracking-[0.08em] text-paper/60">Title</span>
          {TITLES.map((t) => <Chip key={t.label} on={title === t.slug} onClick={() => { setTitle(t.slug); setSel(0) }}>{t.label}</Chip>)}
        </div>
        <div role="group" aria-label="Status" className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-[12px] font-bold uppercase tracking-[0.08em] text-paper/60">Status</span>
          {STATUSES.map((s) => <Chip key={s.label} on={status === s.value} onClick={() => { setStatus(s.value); setSel(0) }}>{s.label}</Chip>)}
        </div>
      </div>

      <section id="desk-results" aria-label="Results" className="overflow-x-auto rounded-xl border border-ink-500 bg-ink-700">
        {!ready && (
          <div className="flex flex-col gap-2 px-6 py-11 text-paper/70">
            <strong className="text-[15px] text-paper">Keep typing</strong>
            <span>Results start after two characters, or one digit for account and check numbers.</span>
          </div>
        )}
        {ready && rows.length > 0 && (
          <div aria-busy={!current} className={`min-w-[860px] ${current ? '' : 'opacity-50'}`}>
            <div className="grid grid-cols-[minmax(0,2.1fr)_96px_minmax(0,2.6fr)_minmax(0,1.7fr)_20px] gap-5 px-5 py-2.5 text-[11px] font-bold uppercase tracking-[0.08em] text-paper/60">
              <span>Member</span><span>Account</span><span>Titles</span><span>Last activity</span><span />
            </div>
            {rows.map((r, i) => {
              const h = highlight(r.name, q)
              return (
                <Link
                  key={r.subscriber_id}
                  to={memberHref(r.subscriber_id)}
                  aria-current={i === sel ? 'true' : undefined}
                  onMouseEnter={() => setSel(i)}
                  className={`grid grid-cols-[minmax(0,2.1fr)_96px_minmax(0,2.6fr)_minmax(0,1.7fr)_20px] items-start gap-5 border-t border-ink-500 px-5 py-3.5 ${focus} ${i === sel ? 'bg-ink-600' : 'hover:bg-ink-600'}`}
                >
                  <span className="flex min-w-0 flex-col gap-[3px]">
                    <span className="text-[15px] font-bold">{h.pre}<mark className="bg-transparent text-amber-glow">{h.hit}</mark>{h.post}</span>
                    <span className="text-[13px] text-paper/70">{r.place}</span>
                    <span className="text-[12px] text-paper/60">
                      {r.email ?? 'No email · postal only'} · <em className="not-italic text-paper/80">matched on {r.matched_on}</em>
                    </span>
                  </span>
                  <span className="font-mono text-[13px]">{r.account_number ? `#${r.account_number}` : ''}</span>
                  <span className="flex flex-wrap gap-1.5">
                    {r.titles.map((t) => (
                      <span key={t.slug} className="inline-flex items-center gap-1.5 rounded-full border border-ink-500 px-2 py-0.5 text-[12px] font-semibold">
                        <span aria-hidden="true" className={`h-[7px] w-[7px] rounded-full ${DOT[t.status]}`} />
                        {t.abbr} · {STATUS_WORD[t.status]}
                        {t.until && <span className="font-normal text-paper/60">{fmtUntil(t.until)}</span>}
                      </span>
                    ))}
                  </span>
                  <span className="flex flex-col gap-[3px] text-[13px]">
                    <span>{r.last_activity ?? ''}</span>
                    {r.pays_by && <span className="text-[12px] text-paper/60">Pays by {r.pays_by}</span>}
                  </span>
                  <span aria-hidden="true" className="text-paper/60">›</span>
                </Link>
              )
            })}
          </div>
        )}
        {showEmpty && (
          <div className="flex flex-col gap-2.5 px-6 py-10 text-paper/70">
            <span>No one matches “{q}”. Try part of a name, a ZIP, or the number printed on the check.</span>
            <Link to={paymentHref('new')} className={`font-semibold text-amber-glow ${focus}`}>Add a new member and record their payment</Link>
          </div>
        )}
      </section>
    </main>
  )
}
